import React, { useState } from 'react';
import { Button } from '../common';
import { Xmark, Check } from 'iconoir-react';
import type { FeedbackReasonTag } from '../../types/match';

interface DeclineModalProps {
  matchId: string;
  otherUserName: string;
  onSubmit: (reasonTags: FeedbackReasonTag[], reasonText: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const REASON_OPTIONS: { tag: FeedbackReasonTag; label: string; icon: string }[] = [
  { tag: 'wrong_industry', label: 'Wrong industry', icon: '🏢' },
  { tag: 'wrong_stage', label: 'Wrong business stage', icon: '📊' },
  { tag: 'bad_timing', label: 'Bad timing', icon: '⏰' },
  { tag: 'not_relevant', label: 'Not relevant to my goals', icon: '🎯' },
  { tag: 'already_connected', label: 'Already connected', icon: '🔗' },
  { tag: 'too_similar', label: 'Too similar to my business', icon: '🔄' },
  { tag: 'too_different', label: 'Too different from my needs', icon: '↔️' },
  { tag: 'location_mismatch', label: 'Location mismatch', icon: '📍' },
  { tag: 'other', label: 'Other reason', icon: '💬' },
];

const DeclineModal: React.FC<DeclineModalProps> = ({
  matchId: _matchId, // Reserved for future analytics
  otherUserName,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [selectedReasons, setSelectedReasons] = useState<FeedbackReasonTag[]>([]);
  const [customReason, setCustomReason] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const toggleReason = (tag: FeedbackReasonTag) => {
    if (tag === 'other') {
      setShowCustomInput(!showCustomInput);
      if (!showCustomInput) {
        setSelectedReasons([...selectedReasons, 'other']);
      } else {
        setSelectedReasons(selectedReasons.filter(r => r !== 'other'));
        setCustomReason('');
      }
    } else {
      setSelectedReasons(prev =>
        prev.includes(tag) ? prev.filter(r => r !== tag) : [...prev, tag]
      );
    }
  };

  const handleSubmit = async () => {
    await onSubmit(selectedReasons, customReason);
  };

  const canSubmit = selectedReasons.length > 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-heading-500">
          Pass on {otherUserName}
        </h3>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Xmark className="w-5 h-5 text-body-400" />
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-body-500 mb-4">
        Help us improve future matches by telling us why this isn't a good fit.
        Select all that apply:
      </p>

      {/* Reason Tags */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {REASON_OPTIONS.map(({ tag, label, icon }) => {
          const isSelected = selectedReasons.includes(tag);
          return (
            <button
              key={tag}
              onClick={() => toggleReason(tag)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-left
                ${isSelected
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-primary-200 text-body-600'
                }
              `}
            >
              <span className="text-base">{icon}</span>
              <span className="text-sm font-medium">{label}</span>
              {isSelected && (
                <Check className="w-4 h-4 ml-auto text-primary-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Custom Reason Input */}
      {showCustomInput && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-body-600 mb-1">
            Tell us more (optional):
          </label>
          <textarea
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="Any additional feedback that would help us..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-none"
            rows={3}
          />
        </div>
      )}

      {/* Privacy Note */}
      <p className="text-xs text-body-400 mb-4">
        Your feedback is private and helps improve your future matches.
        {otherUserName} will not see this.
      </p>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={handleSubmit}
          disabled={!canSubmit || isLoading}
          isLoading={isLoading}
        >
          Pass on Match
        </Button>
      </div>
    </div>
  );
};

export default DeclineModal;
